// src/utils/utm.ts
//
// Campaign parameters of the visit that brought this browser here.
//
// The stash used to be written once and never refreshed or expired, and
// track() spread it onto every GA4 event for the life of the browser profile,
// so a visitor who once arrived from a campaign was booked to that campaign in
// every later session (A-2026-09-21-2). Now:
// - a URL carrying campaign parameters always replaces the stash (latest touch);
// - the stash expires UTM_MAX_AGE_MS after capture, and a stash without a
//   capture time (written by the old code) counts as expired;
// - GA4 events get it only in the browser session that saw the campaign URL,
//   plus the acquisition event (sign_up) - see utmParamsForEvent.
// First-touch attribution for the signup e-mail keeps its own copy
// (attribution.ts), so it does not depend on this stash living forever.

const UTM_STORAGE_KEY = 'utm_payload';
/** Set for the browser session in which the campaign URL was seen. */
const UTM_SESSION_KEY = 'utm_payload_session';
const CAPTURED_AT_KEY = 'captured_at';

/** Same window GA4 uses as its default acquisition lookback for key events. */
export const UTM_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Events that may carry the stash outside the session that captured it. */
const ACQUISITION_EVENTS = new Set(['sign_up']);

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
] as const;

/**
 * Stores the campaign parameters of the current URL, or drops an expired stash
 * when the URL has none. Call once per page load.
 */
export function captureUtm(search: string = window.location.search, now: number = Date.now()): void {
  try {
    const params = new URLSearchParams(search);
    const utm: Record<string, string | number> = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) utm[key] = value;
    }
    if (Object.keys(utm).length) {
      utm[CAPTURED_AT_KEY] = now;
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
      sessionStorage.setItem(UTM_SESSION_KEY, '1');
      return;
    }
    readUtmPayload(now);
  } catch {
    // Private mode / storage disabled - campaign attribution is a nice-to-have.
  }
}

/**
 * The stored campaign parameters (non-empty strings only), or `{}` when there
 * is none or it has expired. An expired or capture-time-less stash is removed.
 */
export function readUtmPayload(now: number = Date.now()): Record<string, string> {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const capturedAt = parsed && typeof parsed === 'object' ? parsed[CAPTURED_AT_KEY] : undefined;
    if (typeof capturedAt !== 'number' || now - capturedAt > UTM_MAX_AGE_MS || capturedAt > now) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const value = parsed[key];
      if (typeof value === 'string' && value) utm[key] = value;
    }
    return utm;
  } catch {
    return {};
  }
}

/**
 * Campaign parameters to attach to a GA4 event: the stash, but only in the
 * session that saw the campaign URL or on an acquisition event. Anything else
 * would re-book an unrelated later session to an old campaign.
 */
export function utmParamsForEvent(eventName: string, now: number = Date.now()): Record<string, string> {
  let campaignSession = false;
  try {
    // Presence is the signal; the value is a plain marker, not a secret.
    campaignSession = Boolean(sessionStorage.getItem(UTM_SESSION_KEY));
  } catch {
    campaignSession = false;
  }
  if (!campaignSession && !ACQUISITION_EVENTS.has(eventName)) return {};
  return readUtmPayload(now);
}
